import { useEffect, useRef, useState } from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    History,
    Loader2,
    Lock,
    MessageSquare,
    Pencil,
    RefreshCw,
    RotateCcw,
    Trash2,
    X,
} from "lucide-react";
import type { ColumnConfig, AuditFlowDocument, TabularCell } from "../shared/types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { preprocessCitations, type ParsedCitation } from "./citation-utils";
import { getPillClass } from "./pillUtils";
import { EmbedPdfViewer } from "../shared/EmbedPdfViewer";
import { DocxView } from "../shared/DocxView";
import { MODELS } from "@/app/components/assistant/ModelToggle";
import type { ApiKeyState } from "@/app/lib/auditflowApi";
import { isModelAvailable, modelGroupToProvider, providerLabel } from "@/app/lib/modelAvailability";
import { useUserProfile } from "@/contexts/UserProfileContext";

function isDocxDocument(d: {
    file_type?: string | null;
    filename?: string;
}): boolean {
    const ft = (d.file_type ?? "").toLowerCase();
    if (ft === "docx" || ft === "doc") return true;
    const ext = d.filename?.split(".").pop()?.toLowerCase();
    return ext === "docx" || ext === "doc";
}

interface Props {
    cell: TabularCell;
    document: AuditFlowDocument;
    column: ColumnConfig;
    columns: ColumnConfig[];
    isLocked?: boolean;
    onClose: () => void;
    onNavigate: (columnIndex: number) => void;
    onRegenerate?: (modelOverride?: string) => Promise<void>;
    onUpdateCell?: (
        content: { summary: string; flag?: "green" | "grey" | "yellow" | "red"; reasoning?: string } | null,
    ) => Promise<void>;
    onUpdateComment?: (comment: string | null) => Promise<void>;
    /** If true, open the document panel immediately */
    displayDocument?: boolean;
    /** Quote to highlight when opening document panel */
    citationQuote?: string;
    /** Page to scroll to when opening document panel */
    citationPage?: number;
}

const FLAG_BADGE: Record<string, string> = {
    green: "bg-emerald-600 backdrop-blur-md border border-emerald-300/20 text-white shadow-md",
    grey: "bg-slate-500 backdrop-blur-md border border-slate-300/20 text-white shadow-md",
    yellow: "bg-amber-500 backdrop-blur-md border border-amber-300/20 text-white shadow-md",
    red: "bg-red-600 backdrop-blur-md border border-red-300/20 text-white shadow-md",
};

// ---------------------------------------------------------------------------
// TRSidePanel
// ---------------------------------------------------------------------------

const FLAG_OPTIONS: Array<{ value: "green" | "grey" | "yellow" | "red"; label: string }> = [
    { value: "green", label: "Green" },
    { value: "yellow", label: "Yellow" },
    { value: "red", label: "Red" },
    { value: "grey", label: "Grey" },
];

function formatEditTimestamp(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export function TRSidePanel({
    cell,
    document: doc,
    column,
    columns,
    isLocked = false,
    onClose,
    onNavigate,
    onRegenerate,
    onUpdateCell,
    onUpdateComment,
    displayDocument = false,
    citationQuote,
    citationPage,
}: Props) {
    const { t } = useTranslation();
    const { profile } = useUserProfile();
    const sortedColumns = [...columns].sort((a, b) => a.index - b.index);
    const currentPos = sortedColumns.findIndex((c) => c.index === column.index);
    const prevColumn = currentPos > 0 ? sortedColumns[currentPos - 1] : null;
    const nextColumn =
        currentPos < sortedColumns.length - 1
            ? sortedColumns[currentPos + 1]
            : null;
    const [regenerating, setRegenerating] = useState(false);
    const [quoteExpanded, setQuoteExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const quoteParagraphRef = useRef<HTMLParagraphElement>(null);

    // Edit mode state
    const [editing, setEditing] = useState(false);
    const [editSummary, setEditSummary] = useState("");
    const [editReasoning, setEditReasoning] = useState("");
    const [editFlag, setEditFlag] = useState<"green" | "grey" | "yellow" | "red" | "">("" );
    const [saving, setSaving] = useState(false);
    const [showOriginal, setShowOriginal] = useState(false);

    // Comment state
    const [commentDraft, setCommentDraft] = useState<string | null>(null);
    const [savingComment, setSavingComment] = useState(false);

    // Model picker state (for per-column override)
    const [modelPickerOpen, setModelPickerOpen] = useState(false);
    const modelPickerRef = useRef<HTMLDivElement>(null);

    const isEdited = !!cell.edited_content;
    const displayContent = cell.edited_content ?? cell.content;
    const apiKeys = profile?.apiKeys;

    // Internal state — initialised from props, also toggled by badge clicks inside the panel
    const [docCitation, setDocCitation] = useState<
        { quote: string; page: number } | undefined
    >(
        displayDocument && citationQuote
            ? { quote: citationQuote, page: citationPage ?? 1 }
            : undefined,
    );

    // Re-sync when the panel opens for a different cell or citation
    useEffect(() => {
        setDocCitation(
            displayDocument && citationQuote
                ? { quote: citationQuote, page: citationPage ?? 1 }
                : undefined,
        );
        setQuoteExpanded(false);
        setEditing(false);
        setShowOriginal(false);
        setCommentDraft(null);
        setModelPickerOpen(false);
    }, [cell.id, displayDocument, citationQuote, citationPage]);

    useEffect(() => {
        if (!modelPickerOpen) return;
        function handleOutside(e: MouseEvent) {
            if (modelPickerRef.current && !modelPickerRef.current.contains(e.target as Node))
                setModelPickerOpen(false);
        }
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [modelPickerOpen]);

    useEffect(() => {
        const el = quoteParagraphRef.current;
        if (!el || quoteExpanded) return;
        setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [docCitation?.quote, quoteExpanded]);

    function startEdit() {
        const base = cell.edited_content ?? cell.content;
        setEditSummary(base?.summary ?? "");
        setEditReasoning(base?.reasoning ?? "");
        setEditFlag((base?.flag ?? "") as "green" | "grey" | "yellow" | "red" | "");
        setEditing(true);
    }

    async function handleSaveEdit() {
        if (!onUpdateCell) return;
        setSaving(true);
        try {
            await onUpdateCell({
                summary: editSummary,
                flag: editFlag || undefined,
                reasoning: editReasoning || undefined,
            });
            setEditing(false);
        } finally {
            setSaving(false);
        }
    }

    async function handleRevert() {
        if (!onUpdateCell) return;
        setSaving(true);
        try {
            await onUpdateCell(null);
            setShowOriginal(false);
        } finally {
            setSaving(false);
        }
    }

    async function handleSaveComment() {
        if (!onUpdateComment) return;
        setSavingComment(true);
        try {
            const val = commentDraft?.trim() || null;
            await onUpdateComment(val);
            setCommentDraft(null);
        } finally {
            setSavingComment(false);
        }
    }

    async function handleDeleteComment() {
        if (!onUpdateComment) return;
        setSavingComment(true);
        try {
            await onUpdateComment(null);
            setCommentDraft(null);
        } finally {
            setSavingComment(false);
        }
    }

    const contentToShow = showOriginal ? cell.content : displayContent;

    const { processed: summaryText, citations: summaryCitations } =
        preprocessCitations(contentToShow?.summary ?? "");
    const { processed: reasoningText, citations: reasoningCitations } =
        preprocessCitations(contentToShow?.reasoning ?? "");

    useEffect(() => {
        console.log("[TRSidePanel] summary:", cell.content?.summary ?? "");
    }, [cell.id, cell.content?.summary]);

    return (
        <div
            className="fixed right-0 top-0 bottom-0 z-100 flex flex-row shadow-md border-l border-gray-200"
            style={{
                background: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(10px) saturate(50%)",
                WebkitBackdropFilter: "blur(10px) saturate(50%)",
            }}
        >
            {/* Document panel — left, 600px */}
            {docCitation !== undefined && (
                <div className="relative flex w-[600px] shrink-0 flex-col border-r border-white/30 px-3">
                    {/* Doc header */}
                    <div className="flex items-center gap-2 pt-3 shrink-0 border-b border-white/30">
                        <p
                            className="flex-1 truncate text-sm font-semibold font-sans text-slate-700 font-serif"
                            title={doc.filename}
                        >
                            {doc.filename}
                        </p>
                        <button
                            onClick={() => setDocCitation(undefined)}
                            className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-white/40 hover:text-slate-600"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    {/* Quote row */}
                    {docCitation.quote && (
                        <div className="py-2 shrink-0">
                            <div className="w-full rounded-md bg-gray-50 border border-gray-200 px-2 py-2">
                                <button
                                    onClick={() =>
                                        isTruncated || quoteExpanded
                                            ? setQuoteExpanded((v) => !v)
                                            : undefined
                                    }
                                    className={`flex w-full items-start gap-1 text-left ${!(isTruncated || quoteExpanded) ? "cursor-default" : ""}`}
                                >
                                    <p
                                        ref={quoteParagraphRef}
                                        className={`flex-1 text-sm text-gray-600 ${quoteExpanded ? "" : "truncate"}`}
                                    >
                                        &ldquo;{docCitation.quote}&rdquo;
                                    </p>
                                    {(isTruncated || quoteExpanded) && (
                                        <ChevronDown
                                            className={`mt-0.5 h-3 w-3 shrink-0 text-gray-500 transition-transform ${quoteExpanded ? "rotate-180" : ""}`}
                                        />
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                    {isDocxDocument(doc) && !doc.pdf_storage_path ? (
                        <DocxView
                            documentId={doc.id}
                            quotes={[
                                {
                                    page: docCitation.page,
                                    quote: docCitation.quote,
                                },
                            ]}
                        />
                    ) : (
                        <EmbedPdfViewer
                            doc={{ document_id: doc.id }}
                            quote={docCitation.quote}
                            fallbackPage={docCitation.page}
                        />
                    )}
                </div>
            )}

            {/* Info column — right, 300px fixed */}
            <div className="flex w-[300px] shrink-0 flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-end gap-3 px-5 pt-3 pb-1 shrink-0 border-b border-white/30">
                    <div className="flex items-center gap-1 mr-auto">
                        <button
                            onClick={() =>
                                prevColumn && onNavigate(prevColumn.index)
                            }
                            disabled={!prevColumn || editing}
                            title={prevColumn ? prevColumn.name : undefined}
                            className="rounded-lg p-0.5 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-900 disabled:opacity-30 disabled:cursor-default"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="text-xs text-slate-600 font-sans tabular-nums">
                            {currentPos + 1} / {sortedColumns.length}
                        </span>
                        <button
                            onClick={() =>
                                nextColumn && onNavigate(nextColumn.index)
                            }
                            disabled={!nextColumn || editing}
                            title={nextColumn ? nextColumn.name : undefined}
                            className="rounded-lg p-0.5 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-900 disabled:opacity-30 disabled:cursor-default"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                    {onUpdateCell && !editing && !isLocked && (
                        <button
                            onClick={startEdit}
                            title="Edit content"
                            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                    )}
                    {isLocked && (
                        <span title="Review is locked" className="rounded-lg p-1.5 text-amber-500">
                            <Lock className="h-4 w-4" />
                        </span>
                    )}
                    {onRegenerate && !editing && !isLocked && (
                        <div ref={modelPickerRef} className="relative flex">
                            <button
                                onClick={async () => {
                                    setRegenerating(true);
                                    try {
                                        await onRegenerate();
                                    } finally {
                                        setRegenerating(false);
                                    }
                                }}
                                disabled={regenerating}
                                title={t("tabular", "regenerate")}
                                className="rounded-l-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40"
                            >
                                {regenerating ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-4 w-4" />
                                )}
                            </button>
                            <button
                                onClick={() => setModelPickerOpen((v) => !v)}
                                disabled={regenerating}
                                title="Re-run with a specific model"
                                className="rounded-r-lg px-0.5 py-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40"
                            >
                                <ChevronDown className={`h-3 w-3 transition-transform ${modelPickerOpen ? "rotate-180" : ""}`} />
                            </button>
                            {modelPickerOpen && (
                                <div className="absolute right-0 top-full mt-1 z-50 w-48 rounded-md border border-gray-200 bg-white shadow-lg py-1 text-xs">
                                    <p className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-400">Re-run with model</p>
                                    {MODELS.map((m) => {
                                        const available = apiKeys ? isModelAvailable(m.id, apiKeys) : true;
                                        return (
                                            <button
                                                key={m.id}
                                                onClick={async () => {
                                                    setModelPickerOpen(false);
                                                    setRegenerating(true);
                                                    try { await onRegenerate(m.id); }
                                                    finally { setRegenerating(false); }
                                                }}
                                                disabled={!available}
                                                title={!available ? `Add a ${providerLabel(modelGroupToProvider(m.group))} API key to use this model` : undefined}
                                                className={`w-full text-left px-3 py-1.5 transition-colors ${available ? "hover:bg-gray-50 text-slate-700" : "text-gray-300 cursor-not-allowed"}`}
                                            >
                                                {m.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    <button
                        onClick={editing ? () => setEditing(false) : onClose}
                        className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Analysis panel */}
                <div className="flex-1 overflow-y-auto">
                    <div className="pb-2 px-5">
                        {/* Column name */}
                        <div className="mb-1">
                            <span className="text-lg font-semibold text-slate-900">
                                {column.name}
                            </span>
                        </div>
                        {/* Document name */}
                        <p className="text-xs mb-4">{doc.filename}</p>

                        {/* Edit metadata */}
                        {isEdited && !editing && (
                            <div className="mb-4 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 flex items-start gap-2">
                                <Pencil className="h-3 w-3 mt-0.5 shrink-0 text-blue-500" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-blue-700">
                                        Edited by <span className="font-medium">{cell.edited_by}</span>
                                    </p>
                                    {cell.edited_at && (
                                        <p className="text-xs text-blue-500 mt-0.5">
                                            {formatEditTimestamp(cell.edited_at)}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-3 mt-1.5">
                                        <button
                                            onClick={() => setShowOriginal((v) => !v)}
                                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                        >
                                            <History className="h-3 w-3" />
                                            {showOriginal ? "Show edited" : "View original"}
                                        </button>
                                        {onUpdateCell && (
                                            <button
                                                onClick={handleRevert}
                                                disabled={saving}
                                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                            >
                                                <RotateCcw className="h-3 w-3" />
                                                Revert to original
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Edit mode */}
                        {editing ? (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">
                                        {t("tabular", "results")}
                                    </label>
                                    <textarea
                                        className="w-full rounded border border-gray-300 text-xs p-2 leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        rows={8}
                                        value={editSummary}
                                        onChange={(e) => setEditSummary(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">
                                        {t("tabular", "flag")}
                                    </label>
                                    <select
                                        className="w-full rounded border border-gray-300 text-xs p-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        value={editFlag}
                                        onChange={(e) => setEditFlag(e.target.value as typeof editFlag)}
                                    >
                                        <option value="">— None —</option>
                                        {FLAG_OPTIONS.map((f) => (
                                            <option key={f.value} value={f.value}>{f.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">
                                        {t("tabular", "reasoning")}
                                    </label>
                                    <textarea
                                        className="w-full rounded border border-gray-300 text-xs p-2 leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        rows={5}
                                        value={editReasoning}
                                        onChange={(e) => setEditReasoning(e.target.value)}
                                    />
                                </div>
                                <div className="flex gap-2 pt-1">
                                    <button
                                        onClick={handleSaveEdit}
                                        disabled={saving || !editSummary.trim()}
                                        className="flex items-center gap-1.5 rounded-md bg-black px-3 py-1.5 text-xs text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {saving ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <Check className="h-3 w-3" />
                                        )}
                                        Save
                                    </button>
                                    <button
                                        onClick={() => setEditing(false)}
                                        disabled={saving}
                                        className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Flag section */}
                                {contentToShow?.flag && (
                                    <div className="mb-5">
                                        <h4 className="mb-2 text-sm font-semibold tracking-wider font-sans">
                                            {t("tabular", "flag")}
                                        </h4>
                                        <span
                                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${FLAG_BADGE[contentToShow.flag] ?? FLAG_BADGE.grey}`}
                                        >
                                            {contentToShow.flag.charAt(0).toUpperCase() +
                                                contentToShow.flag.slice(1)}
                                        </span>
                                    </div>
                                )}

                                {/* Results */}
                                <div className="mb-6">
                                    <h4 className="mb-2 text-sm font-semibold tracking-wider font-sans">
                                        {t("tabular", "results")}
                                    </h4>
                                    <div className="text-xs leading-relaxed text-slate-600">
                                        <MarkdownContent
                                            citations={summaryCitations}
                                            onCitationClick={setDocCitation}
                                            column={column}
                                        >
                                            {summaryText || "—"}
                                        </MarkdownContent>
                                    </div>
                                </div>

                                {/* Reasoning */}
                                {contentToShow?.reasoning && (
                                    <div className="mb-6">
                                        <h4 className="mb-2 text-sm font-semibold tracking-wider font-sans">
                                            {t("tabular", "reasoning")}
                                        </h4>
                                        <div className="text-xs leading-relaxed text-slate-600">
                                            <MarkdownContent
                                                citations={reasoningCitations}
                                                onCitationClick={setDocCitation}
                                                citationOffset={summaryCitations.length}
                                                column={column}
                                                inline
                                            >
                                                {reasoningText}
                                            </MarkdownContent>
                                        </div>
                                    </div>
                                )}

                                {/* Comments section */}
                                {onUpdateComment && (
                                    <div>
                                        <h4 className="mb-2 text-sm font-semibold tracking-wider font-sans flex items-center gap-1.5">
                                            <MessageSquare className="h-3.5 w-3.5" />
                                            Comment
                                        </h4>
                                        {cell.user_comment && commentDraft === null ? (
                                            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                                                <p className="text-xs text-slate-700 whitespace-pre-wrap">{cell.user_comment}</p>
                                                <div className="flex items-center gap-2 mt-1.5">
                                                    <p className="text-[10px] text-slate-400 flex-1">
                                                        {cell.comment_by}{cell.comment_at ? " · " + formatEditTimestamp(cell.comment_at) : ""}
                                                    </p>
                                                    {!isLocked && (
                                                        <>
                                                            <button
                                                                onClick={() => setCommentDraft(cell.user_comment ?? "")}
                                                                className="text-[10px] text-slate-400 hover:text-slate-700"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={handleDeleteComment}
                                                                disabled={savingComment}
                                                                className="text-[10px] text-red-400 hover:text-red-600 disabled:opacity-50"
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ) : commentDraft !== null ? (
                                            <div>
                                                <textarea
                                                    className="w-full rounded border border-gray-300 text-xs p-2 leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                    rows={4}
                                                    value={commentDraft}
                                                    onChange={(e) => setCommentDraft(e.target.value)}
                                                    placeholder="Add a comment…"
                                                    autoFocus
                                                />
                                                <div className="flex gap-2 mt-1">
                                                    <button
                                                        onClick={handleSaveComment}
                                                        disabled={savingComment}
                                                        className="flex items-center gap-1 rounded-md bg-black px-2.5 py-1 text-xs text-white hover:bg-gray-800 disabled:opacity-50"
                                                    >
                                                        {savingComment ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={() => setCommentDraft(null)}
                                                        disabled={savingComment}
                                                        className="rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            !isLocked && (
                                                <button
                                                    onClick={() => setCommentDraft("")}
                                                    className="text-xs text-slate-400 hover:text-slate-700 italic"
                                                >
                                                    + Add a comment…
                                                </button>
                                            )
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Markdown renderer
// ---------------------------------------------------------------------------

function CitationBadge({
    index,
    citation,
    onClick,
}: {
    index: number;
    citation: ParsedCitation;
    onClick: (c: { quote: string; page: number }) => void;
}) {
    return (
        <button
            type="button"
            data-page={citation.page}
            data-quote={citation.quote}
            title={`Page ${citation.page}: "${citation.quote}"`}
            onClick={() =>
                onClick({ quote: citation.quote, page: citation.page })
            }
            className="inline-flex items-center justify-center rounded-full bg-gray-200 w-3.5 h-3.5 text-[9px] font-medium text-gray-700 align-super cursor-pointer hover:bg-gray-300 transition-colors"
        >
            {index + 1}
        </button>
    );
}

function MarkdownContent({
    children,
    citations,
    onCitationClick,
    citationOffset = 0,
    column,
    inline,
}: {
    children: string;
    citations: ParsedCitation[];
    onCitationClick: (c: { quote: string; page: number }) => void;
    inline?: boolean;
    citationOffset?: number;
    column?: ColumnConfig;
}) {
    if (!children) return null;

    const pills: string[] = [];
    let processed = children.replace(/\[\[([^\]]+)\]\]/g, (_, content) => {
        const idx = pills.length;
        pills.push(content);
        return `\`§p${idx}§\``;
    });
    processed = processed.replace(/§(\d+)§/g, (_, idx) => `\`§c${idx}§\``);

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                p: ({ node, ...props }) =>
                    inline ? (
                        <span {...props} />
                    ) : (
                        <p
                            className="mb-1.5 last:mb-0 leading-relaxed"
                            {...props}
                        />
                    ),
                ul: ({ node, ...props }) => (
                    <ul
                        className="list-disc pl-4 space-y-0.5 mb-1.5 last:mb-0"
                        {...props}
                    />
                ),
                ol: ({ node, ...props }) => (
                    <ol
                        className="list-decimal pl-4 space-y-0.5 mb-1.5 last:mb-0"
                        {...props}
                    />
                ),
                li: ({ node, ...props }) => <li {...props} />,
                strong: ({ node, ...props }) => (
                    <strong className="font-semibold" {...props} />
                ),
                em: ({ node, ...props }) => (
                    <em className="italic" {...props} />
                ),
                a: ({ node, href, children, ...props }) => (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline"
                        {...props}
                    >
                        {children}
                    </a>
                ),
                code: ({ node, children: codeChildren, ...props }) => {
                    const t = String(codeChildren);
                    const citMatch = t.match(/^§c(\d+)§$/);
                    if (citMatch) {
                        const idx = parseInt(citMatch[1]);
                        const citation = citations[idx];
                        if (citation) {
                            return (
                                <CitationBadge
                                    index={citationOffset + idx}
                                    citation={citation}
                                    onClick={onCitationClick}
                                />
                            );
                        }
                    }
                    const pillMatch = t.match(/^§p(\d+)§$/);
                    if (pillMatch) {
                        const content = pills[parseInt(pillMatch[1])];
                        if (content !== undefined) {
                            return (
                                <span
                                    className={`inline-block rounded-full px-1.5 py-0.5 text-[11px] font-medium leading-none ${getPillClass(content, column)}`}
                                >
                                    {content}
                                </span>
                            );
                        }
                    }
                    return (
                        <code
                            className="bg-gray-100 px-1 py-0.5 rounded text-[11px] font-mono"
                            {...props}
                        >
                            {codeChildren}
                        </code>
                    );
                },
            }}
        >
            {processed}
        </ReactMarkdown>
    );
}
